import {useState, useRef, useEffect, useMemo} from 'react';
import styled from 'styled-components';
import {raisedSurfaceShadow} from '../../../styles/elevation';
import {useVirtualizer} from '@tanstack/react-virtual';
import type {MentionUser} from '../hooks/useMentionUsers';

interface Props {
	assigneeId: string | null;
	users: MentionUser[];
	onChange: (userId: string | null, note?: string) => void;
	disabled: boolean;
	defaultOpen?: boolean;
}

const ITEM_HEIGHT = 36;
const MAX_VISIBLE = 8;

const Container = styled.div`
	position: relative;
`;

const Trigger = styled.button`
	width: 100%;
	border-radius: 4px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	padding: 4px 8px;
	text-align: left;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #1e293b;
	cursor: pointer;
	&:disabled {
		opacity: 0.5;
		cursor: default;
	}
`;

const Popover = styled.div`
	position: absolute;
	left: 0;
	top: 100%;
	z-index: 50;
	margin-top: 4px;
	width: 256px;
	max-width: calc(100vw - 2rem);
	border-radius: 8px;
	border: 1px solid #e2e8f0;
	background: #ffffff;
	${raisedSurfaceShadow}
`;

const SearchRow = styled.div`
	border-bottom: 1px solid #f1f5f9;
	padding: 8px;
`;

const SearchInput = styled.input`
	width: 100%;
	border-radius: 4px;
	border: 1px solid #e2e8f0;
	padding: 4px 8px;
	${p => p.theme.sys.font?.subheading?.['2']}
	&:focus {
		outline: none;
		box-shadow: 0 0 0 2px #3b82f6;
	}
`;

const OptionButton = styled.button<{$active?: boolean}>`
	display: flex;
	height: 100%;
	width: 100%;
	align-items: center;
	gap: 8px;
	padding: 0 12px;
	border: 0;
	background: ${p => (p.$active ? '#eff6ff' : 'transparent')};
	${p => p.theme.sys.font?.subheading?.['2']}
	font-weight: ${p => (p.$active ? 500 : 400)};
	color: ${p => (p.$active ? '#1d4ed8' : '#1e293b')};
	cursor: pointer;
	&:hover {
		background: #f8fafc;
	}
`;

const UnassignedButton = styled.button`
	display: flex;
	height: 100%;
	width: 100%;
	align-items: center;
	padding: 0 12px;
	border: 0;
	background: transparent;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #64748b;
	cursor: pointer;
	&:hover {
		background: #f8fafc;
	}
`;

const Avatar = styled.span`
	display: inline-flex;
	height: 24px;
	width: 24px;
	flex-shrink: 0;
	align-items: center;
	justify-content: center;
	border-radius: 9999px;
	background: #e0e7ff;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 500;
	color: #4338ca;
`;

const UserName = styled.span`
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
`;

const ConfirmHeader = styled.div`
	border-bottom: 1px solid #f1f5f9;
	padding: 8px 12px;
`;

const ConfirmTitle = styled.p`
	margin: 0;
	${p => p.theme.sys.font?.detail?.['1']}
	font-weight: 600;
	color: #334155;
`;

const ConfirmBody = styled.div`
	padding: 12px;
`;

const NoteLabel = styled.label`
	display: block;
	margin-bottom: 4px;
	${p => p.theme.sys.font?.detail?.['1']}
	color: #64748b;
`;

const NoteTextarea = styled.textarea`
	width: 100%;
	border-radius: 4px;
	border: 1px solid #e2e8f0;
	padding: 4px 8px;
	${p => p.theme.sys.font?.subheading?.['2']}
	color: #1e293b;
	&:focus {
		outline: none;
		box-shadow: 0 0 0 2px #3b82f6;
	}
`;

const ConfirmActions = styled.div`
	margin-top: 8px;
	display: flex;
	gap: 8px;
`;

const ConfirmButton = styled.button`
	flex: 1 1 0%;
	border-radius: 4px;
	border: 0;
	background: #2563eb;
	padding: 4px 12px;
	${p => p.theme.sys.font?.subheading?.['2']}
	font-weight: 500;
	color: #ffffff;
	cursor: pointer;
	&:hover {
		background: #1d4ed8;
	}
`;

const SkipButton = styled.button`
	flex: 1 1 0%;
	border-radius: 4px;
	border: 1px solid #e2e8f0;
	background: transparent;
	padding: 4px 12px;
	${p => p.theme.sys.font?.subheading?.['2']}
	font-weight: 500;
	color: #475569;
	cursor: pointer;
	&:hover {
		background: #f8fafc;
	}
`;

export function AssigneePicker({assigneeId, users, onChange, disabled, defaultOpen = false}: Props) {
	const [open, setOpen] = useState(defaultOpen);
	const [query, setQuery] = useState('');
	const [pendingUserId, setPendingUserId] = useState<string | null | undefined>(undefined);
	const [note, setNote] = useState('');
	const containerRef = useRef<HTMLDivElement>(null);
	const scrollRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	const confirming = pendingUserId !== undefined;

	const currentUser = users.find(u => u.id === assigneeId);
	const triggerLabel = currentUser?.name ?? 'Unassigned';

	const filtered = useMemo(() => {
		if (!query.trim()) return users;
		const lower = query.toLowerCase();
		return users.filter(u => u.name.toLowerCase().includes(lower) || u.email.toLowerCase().includes(lower));
	}, [users, query]);

	const virtualizer = useVirtualizer({
		count: filtered.length + 1,
		getScrollElement: () => scrollRef.current,
		estimateSize: () => ITEM_HEIGHT,
		overscan: 5,
	});

	useEffect(() => {
		if (open && !confirming && inputRef.current) {
			inputRef.current.focus();
		}
		if (confirming && textareaRef.current) {
			textareaRef.current.focus();
		}
	}, [open, confirming]);

	useEffect(() => {
		if (!open) return;
		function handleClickOutside(e: MouseEvent) {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setOpen(false);
				setQuery('');
				setPendingUserId(undefined);
				setNote('');
			}
		}
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, [open]);

	function handleSelect(userId: string | null) {
		if (userId === null) {
			onChange(null, undefined);
			setOpen(false);
			setQuery('');
			return;
		}
		if (userId === assigneeId) {
			setOpen(false);
			setQuery('');
			return;
		}
		setPendingUserId(userId);
		setNote('');
	}

	function handleConfirm() {
		const trimmed = note.trim();
		onChange(pendingUserId!, trimmed.length > 0 ? trimmed : undefined);
		setOpen(false);
		setQuery('');
		setPendingUserId(undefined);
		setNote('');
	}

	function handleSkip() {
		onChange(pendingUserId!, undefined);
		setOpen(false);
		setQuery('');
		setPendingUserId(undefined);
		setNote('');
	}

	const pendingUser = pendingUserId ? users.find(u => u.id === pendingUserId) : null;

	return (
		<Container ref={containerRef}>
			<Trigger type="button" aria-label="Assignee" disabled={disabled} onClick={() => !disabled && setOpen(o => !o)}>
				{triggerLabel}
			</Trigger>

			{open && !confirming && (
				<Popover>
					<SearchRow>
						<SearchInput
							ref={inputRef}
							type="text"
							placeholder="Search users..."
							value={query}
							onChange={e => setQuery(e.target.value)}
						/>
					</SearchRow>
					<div
						ref={scrollRef}
						style={{
							height: Math.min((filtered.length + 1) * ITEM_HEIGHT, MAX_VISIBLE * ITEM_HEIGHT),
							overflow: 'auto',
						}}
					>
						<div style={{height: virtualizer.getTotalSize(), position: 'relative'}}>
							{virtualizer.getVirtualItems().map(virtualItem => {
								const index = virtualItem.index;
								if (index === 0) {
									return (
										<div
											key="unassigned"
											style={{
												position: 'absolute',
												top: 0,
												left: 0,
												width: '100%',
												height: ITEM_HEIGHT,
												transform: `translateY(${virtualItem.start}px)`,
											}}
										>
											<UnassignedButton type="button" onClick={() => handleSelect(null)}>
												Unassigned
											</UnassignedButton>
										</div>
									);
								}
								const user = filtered[index - 1]!;
								return (
									<div
										key={user.id}
										style={{
											position: 'absolute',
											top: 0,
											left: 0,
											width: '100%',
											height: ITEM_HEIGHT,
											transform: `translateY(${virtualItem.start}px)`,
										}}
									>
										<OptionButton
											type="button"
											$active={user.id === assigneeId}
											onClick={() => handleSelect(user.id)}
										>
											<Avatar>
												{user.name
													.split(/\s+/)
													.map(p => p[0])
													.join('')
													.slice(0, 2)
													.toUpperCase()}
											</Avatar>
											<UserName>{user.name}</UserName>
										</OptionButton>
									</div>
								);
							})}
						</div>
					</div>
				</Popover>
			)}

			{open && confirming && (
				<Popover>
					<ConfirmHeader>
						<ConfirmTitle>
							Assign to <strong>{pendingUser?.name ?? pendingUserId}</strong>
						</ConfirmTitle>
					</ConfirmHeader>
					<ConfirmBody>
						<NoteLabel>Handoff note (optional)</NoteLabel>
						<NoteTextarea
							ref={textareaRef}
							value={note}
							onChange={e => setNote(e.target.value)}
							placeholder="Add a note for the new assignee..."
							rows={3}
						/>
						<ConfirmActions>
							<ConfirmButton type="button" onClick={handleConfirm}>
								Confirm
							</ConfirmButton>
							<SkipButton type="button" onClick={handleSkip}>
								Skip
							</SkipButton>
						</ConfirmActions>
					</ConfirmBody>
				</Popover>
			)}
		</Container>
	);
}
